import React, { useRef, useEffect } from "react";
import { Rect, Ellipse, Line, Transformer } from "react-konva";

const ShapeElement = ({
  id,
  x,
  y,
  shapeType,
  width,
  height,
  fill,
  stroke,
  strokeWidth,
  rotation = 0,
  scaleX = 1,
  flipped = false,
  isSelected,
  onSelect,
  onChange,
  drawingMode
}) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && shapeRef.current && trRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleTransform = () => {
    if (!shapeRef.current) return;
    const node = shapeRef.current;

    if (shapeType === 'line') {
      const scaleX = Math.abs(node.scaleX());
      const newWidth = Math.max(5, (width || 0) * scaleX);
      const newRotation = node.rotation();
      node.scaleX(1);
      node.scaleY(1);
      // Store center coordinates directly (no conversion needed)
      const centerX = node.x();
      const centerY = node.y();
      onChange({ x: centerX, y: centerY, width: newWidth, rotation: newRotation });
      return;
    }

    const scaleX = Math.abs(node.scaleX());
    const scaleY = Math.abs(node.scaleY());

    const newWidth = Math.max(10, node.width() * scaleX);
    const newHeight = Math.max(10, node.height() * scaleY);
    const newRotation = node.rotation();

    node.scaleX(1);
    node.scaleY(1);

    const centerX = node.x();
    const centerY = node.y();

    if (shapeType === 'circle') {
      // Circles (ellipses) store center coordinates
      onChange({ x: centerX, y: centerY, width: newWidth, height: newHeight, rotation: newRotation });
    } else {
      // Rectangles store top-left coordinates
      const newX = centerX - newWidth / 2;
      const newY = centerY - newHeight / 2;
      onChange({ x: newX, y: newY, width: newWidth, height: newHeight, rotation: newRotation });
    }
  };

  const renderShape = () => {
    const commonProps = {
      ref: shapeRef,
      id: id,
      draggable: !drawingMode,
      strokeScaleEnabled: false,
      onClick: onSelect,
      onTap: onSelect,
      onTransform: handleTransform,
      onTransformEnd: handleTransform
    };

    switch (shapeType) {
      case 'rectangle':
        return (
          <Rect
            {...commonProps}
            x={x + width / 2}
            y={y + height / 2}
            offsetX={width / 2}
            offsetY={height / 2}
            width={width}
            height={height}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            rotation={rotation || 0}
            scaleX={scaleX || 1}
            onDragEnd={(e) => {
              const cx = e.target.x();
              const cy = e.target.y();
              onChange({ x: cx - width / 2, y: cy - height / 2 });
            }}
          />
        );
      case 'circle':
        return (
          <Ellipse
            {...commonProps}
            x={x}
            y={y}
            radiusX={width / 2}
            radiusY={height / 2}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            rotation={rotation || 0}
            scaleX={scaleX || 1}
            onDragEnd={(e) => {
              const cx = e.target.x();
              const cy = e.target.y();
              onChange({ x: cx, y: cy });
            }}
          />
        );
      case 'line':
        return (
          <Line
            {...commonProps}
            x={x}
            y={y}
            points={[-(width || 0) / 2, 0, (width || 0) / 2, 0]}
            stroke={stroke}
            strokeWidth={strokeWidth}
            rotation={rotation || 0}
            scaleX={scaleX || 1}
            onDragEnd={(e) => {
              const cx = e.target.x();
              const cy = e.target.y();
              onChange({ x: cx, y: cy });
            }}
          />
        );
      default:
        return null;
    }
  };

  const transformerAnchors = shapeType === 'rectangle'
    ? ["top-left", "top-right", "bottom-left", "bottom-right", "top-center", "bottom-center", "middle-left", "middle-right"]
    : shapeType === 'circle'
      ? ["top-left", "top-right", "bottom-left", "bottom-right", "top-center", "bottom-center", "middle-left", "middle-right"]
      : shapeType === 'line'
        ? ["middle-left", "middle-right"]
        : ["top-left", "top-right", "bottom-left", "bottom-right"];

  const rotateEnabled = shapeType === 'line' || shapeType === 'rectangle' || shapeType === 'circle';

  return (
    <>
      {renderShape()}
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled={rotateEnabled}
          keepRatio={false}
          enabledAnchors={transformerAnchors}
          boundBoxFunc={(oldBox, newBox) => {
            if (shapeType === 'line') {
              const minW = 5;
              if (newBox.width < minW) return oldBox;
              return newBox;
            }
            const minSize = 10;
            if (newBox.width < minSize || newBox.height < minSize) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </>
  );
};

export default ShapeElement;
